/**
 * 顾客端菜单渲染：拉取 menu.json、分类/时段/菜品 DOM（列表 vs 栅格）。
 * 依赖 window.RestaurantHtmlTemplates、window.Ordersystem；由 ui-controller 注入状态与回调。
 */
(function (g) {
  'use strict';

  const MEAL_ORDER = ['lunch', 'dinner', 'special'];
  const NAV_ACTIVE =
    'bg-neutral-900 text-white shadow-md ring-1 ring-neutral-900/10 dark:bg-brand-600 dark:ring-brand-500/20';
  const NAV_IDLE =
    'bg-white/90 text-neutral-600 ring-1 ring-neutral-200/90 backdrop-blur-sm hover:bg-white hover:ring-neutral-300 dark:bg-neutral-800/90 dark:text-neutral-300 dark:ring-neutral-600 dark:hover:bg-neutral-800';
  const MEAL_ON = 'bg-brand-600 text-white ring-1 ring-brand-500/30';
  const MEAL_OFF =
    'bg-white/90 text-neutral-600 ring-1 ring-neutral-200/90 dark:bg-neutral-800/90 dark:text-neutral-300 dark:ring-neutral-600';

  function menuJsonBaseHref() {
    try {
      return new URL('./', g.location.href).href;
    } catch {
      return '';
    }
  }

  /** 与 app.js hydrateDefaultsFromServerIfNeeded 一致的 menuSavedRev 合并逻辑 */
  function applyMenuPayloadFromServer(OS, data) {
    const { normalizeMenu, MENU_SERVER_REV_KEY } = OS.menuLib;
    const m = normalizeMenu(data);
    if (!m) return;
    const serverRev =
      data && typeof data.menuSavedRev === 'number' && Number.isFinite(data.menuSavedRev)
        ? data.menuSavedRev
        : null;
    let localRev = 0;
    try {
      const n = parseInt(String(g.localStorage.getItem(MENU_SERVER_REV_KEY) || ''), 10);
      localRev = Number.isFinite(n) ? n : 0;
    } catch {
      localRev = 0;
    }
    const localDishCount = (OS.getMenu().dishes || []).length;
    const serverDishCount = (m.dishes || []).length;
    if (serverRev == null) {
      if (serverDishCount === 0 && localDishCount > 0) return;
      OS.setMenu(m);
      try {
        g.localStorage.setItem(MENU_SERVER_REV_KEY, '0');
      } catch {
        /* ignore */
      }
      return;
    }
    if (serverRev > localRev) {
      OS.setMenu(m);
      try {
        g.localStorage.setItem(MENU_SERVER_REV_KEY, String(serverRev));
      } catch {
        /* ignore */
      }
    }
  }

  async function fetchAndSyncMenuJson(OS) {
    const href = menuJsonBaseHref();
    if (!href || g.location.protocol === 'file:') return;
    try {
      const r = await fetch(new URL('menu.json', href).href, { cache: 'no-store' });
      if (!r.ok) return;
      const data = await r.json();
      applyMenuPayloadFromServer(OS, data);
    } catch {
      /* 离线等：保留 OS.getMenu() */
    }
  }

  /** 菜品网格式卡片；列数见 #menu-items（768–850px 双列、横屏平板三列、竖屏平板双列+抽屉购） */
  function isGridMenuLayout() {
    return true;
  }

  function createCustomerMenuUi(deps) {
    const HtmlT = g.RestaurantHtmlTemplates;
    if (!HtmlT) {
      console.error('[RestaurantUiRender] 缺少 RestaurantHtmlTemplates');
      return { renderMenu: async () => {} };
    }

    const {
      OS,
      esc,
      getActiveCat,
      setActiveCat,
      getCategoryNavActiveId: getCategoryNavActiveIdDep,
      setCategoryNavHighlight: setCategoryNavHighlightDep,
      getActiveMeal,
      setActiveMeal,
      syncActiveMealFromOs,
      refreshMenuAndCart,
      updateLockBanner,
      openDishDetail,
      cartAddBlockedByLimits,
      catName,
      visibleDishes,
      visibleCategories,
      pickValidMealTab,
      mainScrollEl,
      afterMenuDomUpdated,
      afterMenuCategoryActivated,
    } = deps;

    const getCategoryNavActiveId =
      typeof getCategoryNavActiveIdDep === 'function' ? getCategoryNavActiveIdDep : () => getActiveCat();
    const setCategoryNavHighlight =
      typeof setCategoryNavHighlightDep === 'function' ? setCategoryNavHighlightDep : () => {};

    /** 不用 auto-rows-fr：旧 WebKit 上 minmax(0,1fr) 隐式行高偶发塌成 0，菜品“空白” */
    const DISH_GRID_CLASS =
      'grid min-w-0 grid-cols-2 gap-3 sm:gap-3.5 md:gap-4 osTablet:grid-cols-3 osTablet:gap-6 ipadMini2Col:!grid-cols-2';

    const menuSectionDomId = (catId) =>
      'menu-section-' +
      String(catId ?? '')
        .replace(/:/g, '-')
        .replace(/[^a-zA-Z0-9_-]/g, '_');

    let categorySpyOnScroll = null;
    let categorySpyScrollScheduled = false;

    const teardownCategoryScrollSpy = () => {
      const root = mainScrollEl();
      if (root && categorySpyOnScroll) {
        root.removeEventListener('scroll', categorySpyOnScroll);
        categorySpyOnScroll = null;
      }
      categorySpyScrollScheduled = false;
    };

    const runCategoryScrollSpy = () => {
      if (getActiveCat() !== 'all') return;
      const root = mainScrollEl();
      if (!root) return;
      const sections = root.querySelectorAll('[data-menu-cat-section]');
      if (!sections.length) return;
      const prev = getCategoryNavActiveId();
      if (root.scrollTop < 28) {
        const first = visibleCategories()[0];
        const firstId = first != null ? catIdStr(first.id) : '';
        if (firstId && prev !== firstId) {
          setCategoryNavHighlight(firstId);
          renderCategoryLists(OS.getMenu());
        }
        return;
      }
      const rootRect = root.getBoundingClientRect();
      const anchorY = rootRect.top + Math.min(140, rootRect.height * 0.22);
      let best = null;
      let bestScore = -1;
      sections.forEach((sec) => {
        const r = sec.getBoundingClientRect();
        const visTop = Math.max(r.top, rootRect.top);
        const visBot = Math.min(r.bottom, rootRect.bottom);
        const visibleH = visBot - visTop;
        if (visibleH <= 0) return;
        let score = visibleH;
        if (r.top <= anchorY && r.bottom > anchorY) score += 800;
        else if (r.top <= anchorY + 24) score += 400;
        if (score > bestScore) {
          bestScore = score;
          best = sec.getAttribute('data-menu-cat-section');
        }
      });
      if (best && best !== prev) {
        setCategoryNavHighlight(best);
        renderCategoryLists(OS.getMenu());
      }
    };

    const setupCategoryScrollSpy = () => {
      teardownCategoryScrollSpy();
      if (getActiveCat() !== 'all') return;
      const root = mainScrollEl();
      if (!root) return;
      if (!root.querySelector('[data-menu-cat-section]')) return;
      categorySpyOnScroll = () => {
        if (categorySpyScrollScheduled) return;
        categorySpyScrollScheduled = true;
        requestAnimationFrame(() => {
          categorySpyScrollScheduled = false;
          runCategoryScrollSpy();
        });
      };
      root.addEventListener('scroll', categorySpyOnScroll, { passive: true });
      requestAnimationFrame(() => runCategoryScrollSpy());
    };

    const renderComponent = (specs) => {
      for (const { el, html, after } of specs) {
        if (!el) continue;
        el.innerHTML = html;
        if (after) after(el);
      }
    };

    const bindMealClicks = (root) => {
      root.querySelectorAll('[data-meal]').forEach((btn) => {
        btn.addEventListener('click', () => {
          setActiveMeal(btn.getAttribute('data-meal'));
          setActiveCat('all');
          refreshMenuAndCart();
        });
      });
    };

    const safeScrollMainTop = () => {
      const el = mainScrollEl();
      if (!el) return;
      try {
        if (typeof el.scrollTo === 'function') {
          try {
            el.scrollTo({ top: 0, behavior: 'smooth' });
          } catch (_) {
            el.scrollTop = 0;
          }
        } else {
          el.scrollTop = 0;
        }
      } catch (_) {
        try {
          el.scrollTop = 0;
        } catch (_e) {}
      }
    };
    const safeScrollSectionIntoView = (sec) => {
      if (!sec) return;
      try {
        sec.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (_) {
        try {
          sec.scrollIntoView(true);
        } catch (_e) {}
      }
    };

    const bindCatClicks = (root) => {
      root.querySelectorAll('[data-cat]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.getAttribute('data-cat');
          const dismissMenuModal = () => {
            try {
              if (typeof afterMenuCategoryActivated === 'function') afterMenuCategoryActivated();
            } catch (_) {}
          };
          if (getActiveCat() === 'all') {
            const sec = g.document.getElementById(menuSectionDomId(id));
            if (sec) {
              safeScrollSectionIntoView(sec);
              setCategoryNavHighlight(id);
              renderCategoryLists(OS.getMenu());
              dismissMenuModal();
              return;
            }
          }
          setActiveCat(id);
          teardownCategoryScrollSpy();
          void renderMenu();
          safeScrollMainTop();
          dismissMenuModal();
        });
      });
    };

    const renderMealRow = () => {
      const rowM = g.document.getElementById('customer-meal-row-mobile');
      const rowT = g.document.getElementById('customer-meal-row-tablet');
      const s = OS.getSystemSettings();
      syncActiveMealFromOs();
      pickValidMealTab();
      const hideMealRows = () => {
        [rowM, rowT].forEach((row) => {
          if (!row) return;
          row.classList.add('hidden');
          row.innerHTML = '';
        });
      };
      if (!OS.systemSettingsUseMealProfiles(s)) {
        hideMealRows();
        return;
      }
      const ov = s.customerMealOverride != null ? String(s.customerMealOverride).trim() : '';
      if (ov === 'lunch' || ov === 'dinner' || ov === 'special') {
        hideMealRows();
        return;
      }
      const mealUi = { lunch: 'customer.mealLunch', dinner: 'customer.mealDinner', special: 'customer.mealSpecial' };
      const tabs = MEAL_ORDER.map((id) => ({ id, name: OS.uiT(mealUi[id]) }));
      const mealBtn = (t, ti, cls) =>
        HtmlT.roleTabButton(esc, {
          dataAttr: 'data-meal',
          value: t.id,
          delayIdx: ti,
          delayStepMs: 35,
          classBase: `${cls} shadow-sm transition-all duration-200 active:scale-95 active:opacity-70`,
          activeClass: MEAL_ON,
          idleClass: MEAL_OFF,
          isActive: getActiveMeal() === t.id,
          label: t.name,
        });
      const htmlMobile = tabs
        .map((t, ti) =>
          mealBtn(
            t,
            ti,
            'min-h-9 w-full rounded-lg px-1 py-2 text-[0.9rem] font-bold leading-tight sm:text-[1rem]'
          )
        )
        .join('');
      const htmlTablet = tabs
        .map((t, ti) =>
          mealBtn(
            t,
            ti,
            'min-h-10 w-full rounded-xl px-3 py-2 text-left text-sm font-medium leading-snug osTabletLand:min-h-11 osTabletLand:py-2.5 osTabletLand:text-base osTabletLand:font-semibold osTabletLand:uppercase osTabletLand:tracking-wide osTabletLand:shadow-sm'
          )
        )
        .join('');
      if (rowM) {
        rowM.classList.remove('hidden');
        rowM.innerHTML = htmlMobile;
        bindMealClicks(rowM);
      }
      if (rowT) {
        rowT.classList.remove('hidden');
        rowT.innerHTML = htmlTablet;
        bindMealClicks(rowT);
      }
    };

    const catIdStr = (v) => String(v ?? '');

    const renderCategoryLists = (_menuJson) => {
      const $categories = g.document.getElementById('categories');
      const tabs = visibleCategories().map((c) => ({ id: catIdStr(c.id), name: OS.categoryLabelForUi(c) }));
      let ac = getActiveCat();
      if (ac !== 'all' && !tabs.some((t) => catIdStr(t.id) === catIdStr(ac))) {
        setActiveCat('all');
        ac = 'all';
      }
      const catBtnCls =
        'shrink-0 rounded-full px-3 py-2 text-[0.95rem] font-medium uppercase tracking-wide whitespace-nowrap shadow-md transition-all duration-200 active:scale-95 active:opacity-90 sm:px-4 sm:py-2.5 sm:text-[1.05rem] osTablet:w-full osTablet:rounded-xl osTablet:px-2.5 osTablet:py-2 osTablet:text-left osTablet:text-[0.96rem] osTablet:font-normal osTablet:leading-snug osTablet:whitespace-normal osTablet:shadow-sm osTabletLand:py-2.5 osTabletLand:text-lg osTabletLand:font-semibold' +
        " osTablet:relative osTablet:pl-6 osTablet:before:absolute osTablet:before:left-2 osTablet:before:top-1/2 osTablet:before:-translate-y-1/2 osTablet:before:text-xs osTablet:before:font-normal osTablet:before:text-neutral-400 osTablet:before:content-['\u00bb'] dark:osTablet:before:text-neutral-500";
      const btn = (t, ti) =>
        HtmlT.roleTabButton(esc, {
          dataAttr: 'data-cat',
          value: t.id,
          delayIdx: ti,
          delayStepMs: 40,
          classBase: catBtnCls,
          activeClass: NAV_ACTIVE,
          idleClass: NAV_IDLE,
          isActive: catIdStr(getCategoryNavActiveId()) === catIdStr(t.id),
          label: t.name,
        });
      renderComponent([
        {
          el: $categories,
          html: tabs.map((t, ti) => btn(t, ti)).join(''),
          after: bindCatClicks,
        },
      ]);
    };

    const bindDishCardOpens = ($root) => {
      $root.querySelectorAll('[data-dish-detail-trigger]').forEach((btn) => {
        const id = btn.getAttribute('data-dish-detail-trigger');
        const open = () => {
          if (OS.getOrderCooldownRemainingMs() > 0) return;
          openDishDetail(id);
        };
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          open();
        });
      });
    };

    const renderDishCardsFromMenu = (menuRef) => {
      const $grid = g.document.getElementById('menu-items');
      if (!$grid) return;
      const dishes = visibleDishes();
      const ac = getActiveCat();
      const list = ac === 'all' ? dishes : dishes.filter((d) => catIdStr(d.category) === catIdStr(ac));
      const blockAdd =
        OS.getOrderCooldownRemainingMs() > 0 ||
        (typeof cartAddBlockedByLimits === 'function' ? cartAddBlockedByLimits() : false);
      if (!list.length) {
        teardownCategoryScrollSpy();
        $grid.innerHTML = HtmlT.customerEmptyCategoryMessage(esc, OS.uiT('customer.emptyCategory'));
        return;
      }
      const gridLocked = OS.getOrderCooldownRemainingMs() > 0;
      const oneCard = (d, i) =>
        HtmlT.customerDishCardArticle(esc, {
          id: d.id,
          index: i,
          gridLocked,
          imageUrl: OS.resolveCustomerMediaUrl(d.imageUrl || d.img || ''),
          categoryLine: catName(d.category),
          categoryId: d.category,
          title: OS.dishNameForUiLang(menuRef, d),
          desc: String(OS.dishDescriptionForUiLang(menuRef, d) ?? '').trim(),
          price: d.price,
          blockAdd,
          foodClass: d.foodClass,
          foodClassLabel: String(OS.dishFoodClassLabelForUi(d) ?? '').trim(),
        });

      if (ac !== 'all') {
        teardownCategoryScrollSpy();
        $grid.innerHTML = list.map((d, i) => oneCard(d, i)).join('');
        bindDishCardOpens($grid);
        return;
      }

      const sectionFn = HtmlT.customerMenuCategorySection;
      if (typeof sectionFn !== 'function') {
        teardownCategoryScrollSpy();
        $grid.innerHTML = list.map((d, i) => oneCard(d, i)).join('');
        bindDishCardOpens($grid);
        return;
      }

      const cats = visibleCategories();
      const parts = [];
      let idx = 0;
      cats.forEach((c) => {
        const catDishes = dishes.filter((d) => catIdStr(d.category) === catIdStr(c.id));
        if (!catDishes.length) return;
        const cardsHtml = catDishes.map((d) => oneCard(d, idx++)).join('');
        parts.push(
          sectionFn(esc, {
            domId: menuSectionDomId(c.id),
            catId: c.id,
            title: OS.categoryLabelForUi(c),
            gridClass: DISH_GRID_CLASS,
            cardsHtml,
          })
        );
      });
      if (!parts.length) {
        teardownCategoryScrollSpy();
        $grid.innerHTML = HtmlT.customerEmptyCategoryMessage(esc, OS.uiT('customer.emptyCategory'));
        return;
      }
      $grid.innerHTML = parts.join('');
      bindDishCardOpens($grid);
      setupCategoryScrollSpy();
    };

    async function renderMenu() {
      await fetchAndSyncMenuJson(OS);
      syncActiveMealFromOs();
      const menuJson = OS.getMenu();
      if (!menuJson || typeof menuJson !== 'object') return;
      renderMealRow();
      renderCategoryLists(menuJson);
      renderDishCardsFromMenu(menuJson);
      afterMenuDomUpdated?.();
      updateLockBanner();
    }

    return { renderMenu };
  }

  g.RestaurantUiRender = {
    createCustomerMenuUi,
    fetchAndSyncMenuJson,
    applyMenuPayloadFromServer,
    isGridMenuLayout,
  };

  if (g.OS && typeof g.OS.registerModule === 'function') {
    try { g.OS.registerModule('render', g.RestaurantUiRender); } catch (_) {}
  }
})(typeof globalThis !== 'undefined' ? globalThis : window);
